'use client';

import { useEffect, useMemo, useState } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';
import { entryIsActive, type NavEntry } from './navEntry';
import { subMenuPages } from './navGroups';

/** Row highlighting uses entryIsActive at the live row. This hook selects the exact page
 * and opens its containing sub-menu from the pathname and the supported ?cat= address.
 * Fragments are stripped from hrefs; row anchors remain owned by useRowAnchor. */

/** The addressable parts of a nav href. A bare path carries neither of the other two. */
interface HrefParts {
  path: string;
  /** The deck section, `?cat=` — the one query parameter navigation addresses a page by. Every other
   *  parameter (`?row=`, a filter, a token) is state within a page and must not split it into two rows. */
  cat: string | null;
}

function parseHref(href: string): HrefParts {
  const [beforeHash = ''] = href.split('#');
  const [path = '', query = ''] = beforeHash.split('?');
  const cat = query ? new URLSearchParams(query).get('cat') : null;
  return { path, cat };
}

function pathContains(route: string, pathname: string): boolean {
  return pathname === route || pathname.startsWith(`${route}/`);
}

/** How well an href describes the location — higher is more specific, 0 is no match at all.
 *
 *  The score is the path's own length plus a large constant per addressing part the href actually names
 *  and the location confirms. That ordering matters: `/settings?cat=models` must beat plain `/settings`
 *  while the reader is on the models section, and `/settings` must still win over nothing once they are
 *  on a section no row names. */
/** @internal web/tests/components/shell/sidebarRoute.test.ts checks href specificity. */
export function hrefMatchScore(href: string, pathname: string, search: string): number {
  const target = parseHref(href);
  if (!pathContains(target.path, pathname)) return 0;
  if (target.cat !== null) {
    const current = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search).get('cat');
    if (current !== target.cat) return 0;
  }
  const specificity = target.cat === null ? 0 : 1000;
  return specificity + target.path.length + (pathname === target.path ? 1 : 0);
}

export interface SidebarRoute {
  /** The one href in the column that IS the current page. */
  currentHref?: string;
  /** The entry whose sub-menu the route forces open, so the current page is never hidden inside a fold. */
  openId?: string;
}

/** @internal web/tests/components/shell/sidebarRoute.test.ts checks page and submenu selection. */
export function resolveSidebarRoute(
  entries: readonly NavEntry[],
  pathname: string,
  search = '',
): SidebarRoute {
  const active = entries.find((entry) => entryIsActive(entry, pathname));
  if (!active) return {};
  const candidates = [
    ...(active.href === undefined ? [] : [active.href]),
    ...(active.subItems ?? []).map((page) => page.href),
  ];
  let currentHref: string | undefined;
  let best = 0;
  for (const href of candidates) {
    const score = hrefMatchScore(href, pathname, search);
    if (score > best) { best = score; currentHref = href; }
  }
  const route: SidebarRoute = {};
  if (currentHref !== undefined) route.currentHref = currentHref;
  if (subMenuPages(active) && active.id !== undefined) route.openId = active.id;
  return route;
}

/** The live query string, from three readings that each answer part of the question.
 *
 *  `useSearchParams` is the ROUTER's answer and the one that updates when a sub-item is clicked. It reads
 *  EMPTY on a statically optimized route until that first client navigation, which is why the document's
 *  own URL is read on arrival as well — the same pair `lib/useRowAnchor.ts` reads, for the same reason.
 *  That arrival reading is a FALLBACK only: it is used while the router has nothing to say, so it can
 *  never pin the column to the address the tab was opened at.
 *
 *  The third reading is the one a deck page ANNOUNCES. Switching section from inside the page (a
 *  cross-link between two settings sections) rewrites the address with `history.replaceState` and fires a
 *  synthetic `popstate`, because the router never learns about a history entry it did not write. While
 *  such an announcement stands it wins outright — it is the document's actual URL and nothing else knows
 *  about it — and it is retired the moment the router moves, so it cannot outlive the section it named. */
function useLocation(): { search: string } {
  const searchParams = useSearchParams();
  const routerSearch = searchParams.toString();
  const pathname = usePathname();
  const [arrival, setArrival] = useState<{ search: string }>({ search: '' });
  useEffect(() => {
    setArrival({ search: window.location.search.replace(/^\?/, '') });
  }, [pathname]);

  // The announcement carries the route it was made on, so a router navigation retires it without needing
  // a second effect to clear it.
  const route = `${pathname}?${routerSearch}`;
  const [announced, setAnnounced] = useState<{ search: string; route: string } | null>(null);
  useEffect(() => {
    const read = () => setAnnounced({ search: window.location.search.replace(/^\?/, ''), route });
    window.addEventListener('popstate', read);
    return () => window.removeEventListener('popstate', read);
  }, [route]);
  const live = announced !== null && announced.route === route ? announced : null;

  return {
    search: live ? live.search : routerSearch === '' ? arrival.search : routerSearch,
  };
}

export function useSidebarRoute(entries: readonly NavEntry[]): SidebarRoute {
  const pathname = usePathname();
  const { search } = useLocation();
  return useMemo(() => resolveSidebarRoute(entries, pathname, search), [entries, pathname, search]);
}
